import { cloneDeep, get } from 'lodash';
import { authorizedFetch } from '../utils';
import { getElasticTimeZoneOffset, NESTED_FIELDS } from '../reporting-utils';
import { getQuery, resolveDependencies } from './report-renderer';
import { getGroupProperty } from '../flows/flow-report-utils';

const NO_VALUE = 'No value';

const getIncludedTermValues = (termsFilters, groupField) => {
  for (let i = 0; i < termsFilters.length; i++) {
    const tf = termsFilters[i];

    if (tf.terms && tf.terms[groupField]) {
      return tf.terms[groupField];
    }
    if (tf.nested && tf.nested.query.terms[groupField]) {
      return tf.nested.query.terms[groupField];
    }
  }
};

export const assignElasticResultsToRow = (measures, row, bucket) => {
  measures
    .filter(m => m.esValueAccessor)
    .forEach(m => {
      row[m.property] = get(bucket, m.esValueAccessor, 0);
    });
};

const getFlattenedRows = (
  aggs,
  bucketNames,
  measures = [],
  parentBuckets = []
) => {
  const rows = [];

  if (bucketNames.length > 0) {
    bucketNames.forEach((g, i) => {
      if (aggs[g]) {
        const isNestedBucket = !!aggs[g].nestedField;
        const aggBuckets = isNestedBucket
          ? aggs[g].nestedField.buckets
          : aggs[g].buckets;

        if (Array.isArray(aggBuckets)) {
          aggBuckets.forEach((b, bi) => {
            const currentBucketKey = b.key_as_string || b.key;

            //if we are in a nested metric, we need to resolve to the parent entity that has the reverse nested aggregate
            const bucketWithData = b.parent || b;

            const row = {};
            const keyParts = [];

            //make sure to append every kvp of parent groupings to this row, since the tree will only have the metric data at this level
            //ex: campaignId: 123
            //    adGroupId: 456
            parentBuckets.forEach((pb, pbI) => {
              row[pb.field] = pb.value;
              //parent grouping on a date, remove the NYC timezone from the bucket key
              if (pb.field === 'timeStamp' || pb.field === 'timeStampHourly') {
                keyParts[pbI] = pb.value.split('.')[0];
              } else {
                keyParts[pbI] = pb.value;
              }
            });

            //grouping on a date, remove the NYC timezone from the bucket key
            keyParts.push(
              g === 'timeStamp' || g === 'timeStampHourly'
                ? currentBucketKey.split('.')[0]
                : currentBucketKey
            );
            row[g] = currentBucketKey;

            assignElasticResultsToRow(measures, row, bucketWithData);

            //if there are subsequent groupings, process them recursively
            if (bucketNames.length > 1) {
              //if we have multiple levels left to traverse, set the column value and key value for those levels to be 'All'
              bucketNames.forEach((bN, bNi) => {
                if (bNi > 0) {
                  row[bN] = 'All';
                  keyParts.push('All');
                }
              });

              row.key = keyParts.join('.');
              rows.push(row);

              //process recursively
              const remainingBuckets = [...bucketNames.slice(1)];

              rows.push(
                ...getFlattenedRows(
                  bucketWithData,
                  remainingBuckets,
                  measures,
                  [...parentBuckets, { field: g, value: currentBucketKey }]
                )
              );
            } else {
              row.key = keyParts.join('.');
              rows.push(row);
            }
          });
        }
      }
    });
  }

  return rows;
};

export const getTermsCDSData = async (
  groups,
  fields,
  termsFilters,
  range,
  interval,
  bounds,
  isPreview,
  queryEventUrl,
  setReportData,
  setLoading,
  addError,
  paramCache,
  signal,
  additionalMusts,
  additionalMustNots
) => {
  setLoading(true);
  const query = getQuery({
    range,
    termsFilters,
    additionalMusts,
    additionalMustNots
  });

  const previewFilter = {
    prefix: { scope: 'preview_' }
  };

  if (isPreview) {
    query.query.bool.must.push(previewFilter);
  } else {
    query.query.bool.must_not.push(previewFilter);
  }

  const groupAggregation = {};
  const groupProperties = groups;

  let measureAggs;
  let fieldsToGrab = fields.map(f => ({
    depth: 0,
    field: f
  }));

  if (fields.length > 0) {
    measureAggs = {};

    await resolveDependencies(fields, measureAggs, paramCache, fieldsToGrab);

    //sort based on dependency depth
    fieldsToGrab.sort((a, b) => b.depth - a.depth);

    //remove duplicates
    fieldsToGrab = fieldsToGrab.reduce((acc, curr) => {
      if (!acc.find(f => f.property === curr.field.property)) {
        acc.push(curr.field);
      }
      return acc;
    }, []);
  }

  let innerMostAgg;
  groupProperties.reduce((parent, g) => {
    let groupAgg;
    const nestedField = NESTED_FIELDS.find(nf => nf.field === g);

    if (g === 'timeStamp' || g === 'timeStampHourly') {
      groupAgg = {
        date_histogram: {
          field: 'timeStamp',
          fixed_interval: interval,
          min_doc_count: 0,
          time_zone: getElasticTimeZoneOffset(),
          extended_bounds: bounds
        }
      };
    } else {
      groupAgg = {
        terms: {
          field: g,
          size: 1500,
          include: getIncludedTermValues(termsFilters, g),
          missing: NO_VALUE
        }
      };

      //if grouping on a nested field, need to push aggs down one level underneath a nested agg bucket
      if (nestedField) {
        groupAgg = {
          nested: {
            path: nestedField.path
          },
          aggs: {
            nestedField: groupAgg
          }
        };
      }
    }

    const groupKey = getGroupProperty(g);
    parent.aggs = parent.aggs || {};

    //if our parent group is a nested field, we need to add our child aggs down one level under nested bucket
    if (parent.nested) {
      parent.aggs.nestedField.aggs.parent.aggs[groupKey] = groupAgg;
    } else {
      parent.aggs[groupKey] = groupAgg;
    }

    innerMostAgg = groupAgg;
    //get the ALL metric for each group
    if (measureAggs) {
      //if we are grouping on a nested type, we need to generate a child aggregation that can perform metrics on the parent document type
      if (nestedField) {
        groupAgg.aggs.nestedField.aggs = {
          parent: {
            reverse_nested: {},
            aggs: cloneDeep(measureAggs)
          }
        };
        return groupAgg.aggs.nestedField.aggs.parent;
      } else if (parent.nested) {
        //if our parent is a nested agg, we need to resolve to the parent document type as well
        groupAgg.aggs = {
          parent: {
            reverse_nested: {},
            aggs: cloneDeep(measureAggs)
          }
        };
        return groupAgg.aggs.parent;
      } else {
        groupAgg.aggs = cloneDeep(measureAggs);
      }
    }
    return groupAgg;
  }, groupAggregation);

  if (innerMostAgg) {
    query.aggs = groupAggregation.aggs;
    Object.assign(query.aggs, measureAggs);
  } else {
    query.aggs = measureAggs;
  }

  authorizedFetch(queryEventUrl, 'POST', query, signal)
    .then(response => {
      if (query.aggs && !response.aggregations) {
        throw new Error('No Aggregations found');
      }

      const mappedGroupFields = groupProperties.map(gp => getGroupProperty(gp));

      const measures = Array.from(fieldsToGrab);
      const totalRow = { key: 'totalRow' };
      mappedGroupFields.forEach(gf => {
        totalRow[gf] = 'All';
      });

      const rows = [totalRow];

      if (response.aggregations) {
        assignElasticResultsToRow(measures, totalRow, response.aggregations);
        rows.push(
          ...getFlattenedRows(
            response.aggregations,
            mappedGroupFields,
            measures
          )
        );
      }
      setReportData(rows);
      setLoading(false);
    })
    .catch(e => {
      if (e.name !== 'AbortError') {
        if (e.message) {
          addError('Unable to get report data', JSON.stringify(e.message));
        }
        if (
          e.error?.caused_by?.type === 'too_many_buckets_exception' ||
          e.error?.root_cause.some(
            cause => cause.type === 'too_many_buckets_exception'
          )
        ) {
          addError(
            'Unable to get report data',
            'Report generated too many row buckets. Try a different configuration.'
          );
        }
        setLoading(false);
      }
    });
};
